import React, { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { ApiError, api } from '../../lib/api'
import { useAuth } from '../../context/AuthContext'
import { AlreadySignedInNotice } from '../../components/AlreadySignedInNotice'
import type { PricingPlans } from '../../lib/types'

type Step = 'account' | 'practice' | 'review' | 'enterprise'

// Organization size determines the plan automatically — the user is never
// asked to pick Solo/Group/Clinic manually. Enterprise (51+) isn't self-serve
// (see backend SELF_SERVE_TIERS), so it branches to a "talk to sales" step
// instead of continuing the signup form.
function determineTier(providerCount: number): string {
  if (providerCount <= 1) return 'solo_provider'
  if (providerCount <= 10) return 'group_practice'
  if (providerCount <= 50) return 'clinic'
  return 'enterprise'
}

export default function Signup() {
  const navigate = useNavigate()
  const { me, loginWithToken } = useAuth()
  const [step, setStep] = useState<Step>('account')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [plans, setPlans] = useState<PricingPlans>({})

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const [practiceName, setPracticeName] = useState('')
  const [website, setWebsite] = useState('')
  const [providerCount, setProviderCount] = useState('')

  const [practiceType, setPracticeType] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)

  useEffect(() => {
    api('/pricing-plans').then(setPlans).catch(() => {})
  }, [])

  function next(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (step === 'account') {
      if (password.length < 8) {
        setError('Password must be at least 8 characters.')
        return
      }
      setStep('practice')
    } else if (step === 'practice') {
      const count = Number(providerCount)
      if (!providerCount || !Number.isInteger(count) || count < 1) {
        setError('Enter the number of providers to continue.')
        return
      }
      const tier = determineTier(count)
      setPracticeType(tier)
      setStep(tier === 'enterprise' ? 'enterprise' : 'review')
    }
  }

  function back() {
    setError(null)
    if (step === 'practice') setStep('account')
    else if (step === 'review') setStep('practice')
    else if (step === 'enterprise') setStep('practice')
  }

  async function submit() {
    setError(null)
    if (!acceptedTerms) {
      setError('You must agree to the Terms of Service and acknowledge the Privacy Policy to continue.')
      return
    }
    setBusy(true)
    try {
      const result = await api('/signup', {
        method: 'POST',
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          email,
          password,
          practice_name: practiceName,
          website: website || null,
          expected_provider_count: providerCount ? Number(providerCount) : null,
          practice_type: practiceType,
          accepted_terms: acceptedTerms,
        }),
      })
      await loginWithToken(result.access_token)
      navigate('/practice', { replace: true })
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || 'Could not create your account.')
      } else {
        setError('Something went wrong. Try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  if (me) return <AlreadySignedInNotice continueAs="a new practice" />

  return (
    <div className="auth-shell warm-theme">
      <div className="auth-card">
        <h1>Set up your practice</h1>
        {step !== 'enterprise' && (
          <p className="muted" style={{ fontSize: 13.5 }}>
            Step {['account', 'practice', 'review'].indexOf(step) + 1} of 3
          </p>
        )}

        {step === 'account' && (
          <form onSubmit={next}>
            <div className="form-grid">
              <div className="field"><label>First name</label><input className="inp" value={firstName} onChange={(e) => setFirstName(e.target.value)} required autoFocus /></div>
              <div className="field"><label>Last name</label><input className="inp" value={lastName} onChange={(e) => setLastName(e.target.value)} required /></div>
            </div>
            <div className="field"><label>Email</label><input className="inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
            <div className="field"><label>Password</label><input className="inp" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} /></div>
            {error && <div className="form-error" role="alert">{error}</div>}
            <button className="btn full" type="submit">Continue</button>
          </form>
        )}

        {step === 'practice' && (
          <form onSubmit={next}>
            <div className="field"><label>Practice name</label><input className="inp" value={practiceName} onChange={(e) => setPracticeName(e.target.value)} required autoFocus /></div>
            <div className="field"><label>Website (optional)</label><input className="inp" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" /></div>
            <div className="field">
              <label>Number of providers</label>
              <input className="inp" type="number" min={1} value={providerCount} onChange={(e) => setProviderCount(e.target.value)} required />
              <p className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>We'll set up the right plan for your team size automatically.</p>
            </div>
            <p className="legal-note">
              If your practice will use ATUUNE to process protected health information, a Business
              Associate Agreement (BAA) may be required before doing so. Contact us at{' '}
              <a href="mailto:contact@atuune.com">contact@atuune.com</a> to request one.
            </p>
            {error && <div className="form-error" role="alert">{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn outline" type="button" onClick={back}>Back</button>
              <button className="btn full" type="submit">Continue</button>
            </div>
          </form>
        )}

        {step === 'enterprise' && (
          <div>
            <p className="muted" style={{ fontSize: 13.5 }}>
              Organizations with 51+ providers get custom pricing, dedicated onboarding, and multi-location
              support. Let's talk through what you need.
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn outline" type="button" onClick={back}>Back</button>
              <Link className="btn full" to="/contact" style={{ textAlign: 'center' }}>Talk to Sales</Link>
            </div>
          </div>
        )}

        {step === 'review' && (
          <div>
            <div className="row-list" style={{ marginBottom: 14 }}>
              <div className="row"><span>Name</span><span className="muted">{firstName} {lastName}</span></div>
              <div className="row"><span>Email</span><span className="muted">{email}</span></div>
              <div className="row"><span>Practice</span><span className="muted">{practiceName}</span></div>
              {website && <div className="row"><span>Website</span><span className="muted">{website}</span></div>}
              {providerCount && <div className="row"><span>Providers</span><span className="muted">{providerCount}</span></div>}
              <div className="row"><span>Plan</span><span className="muted">{plans[practiceType]?.label || practiceType}</span></div>
            </div>
            <div className="field" style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 8 }}>
              <input type="checkbox" id="acceptedTerms" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} style={{ marginTop: 3 }} aria-required="true" />
              <label htmlFor="acceptedTerms" style={{ margin: 0, fontSize: 13.5 }}>
                I have read and agree to the <Link to="/terms" target="_blank">Terms of Service</Link> and acknowledge the <Link to="/privacy" target="_blank">Privacy Policy</Link>.
              </label>
            </div>
            {error && <div className="form-error" role="alert">{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn outline" type="button" onClick={back} disabled={busy}>Back</button>
              <button className="btn full" type="button" onClick={submit} disabled={busy}>{busy ? 'Creating your account…' : 'Create organization'}</button>
            </div>
          </div>
        )}

        <p className="muted" style={{ fontSize: 13, marginTop: 16 }}>
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  )
}
